import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { useUserData } from 'components/contexts/UserDataContext'
import { FIXED_DECIMAL_PLACES, bigFromFee } from 'utils/price'
import { useCart, usePurchaseFlowConfig } from 'components/pages/PurchaseFlow/_main/contexts'

import { Billing } from 'components/pages/PurchaseFlow/common/Billing'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { isShootingCategory } from 'utils/validators'
import { useAuth0 } from 'utils/auth'
import { useCallback, useEffect, useRef } from 'react'
import { usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts/PurchaseFlowProducts.context'
import { useTargetOrderUser } from '../../../_main/contexts'
import { useTranslation } from 'react-i18next'
import { ScreenEntryLayout, UNMEASURED_SCREEN_ENTRY_LAYOUT, measureScreenEntryLayout } from 'utils/analytics/screenEntryLayout'
import { InvoiceOption, resolveInvoiceOption } from 'components/pages/PurchaseFlow/common/Billing/invoiceOption'
import { logger } from 'utils/logging'

/** How long the billing options may take before the enters-screen event goes out with the invoice option unknown. */
export const BILLING_OPTIONS_SETTLE_TIMEOUT_MS = 10_000

/**
 * @component Wrapper and main controller for billing step part of PF 
 * @example <BillingController />
 */
export const BillingStepController: React.FC = () => {
  const { t } = useTranslation(['order', 'product'])

  const { roles } = useAuth0()
  const { targetUser, adminSelectedUserEmail, orderWorkspaceId } = useTargetOrderUser()
  const { isWorkspaceResolved } = useUserData()
  const { total } = useCart()

  const {
    selectedCategory,
    sessionId,
    selectedCountryCode,
    selectedAssignmentPlace,
    selectedCity,
    getInstructionCatalogue,
    isInstructionStepSkipped,
    availablePaymentMethods,
  } = usePurchaseFlowConfig()

  const {
    selectedProducts,
    selectedProductsKey,
    selectedSegment,
    selectedKinds,
  } = usePurchaseFlowProducts()

  /** Retrieves the configuration from the API  */
  useEffect(() => {
    // Mutation is called in Instruction step, if Instruction step is rendered, we don't need to call it again
    if (!isInstructionStepSkipped) return
    if (!getInstructionCatalogue.isIdle) return
    if (!selectedCategory || !sessionId) return
    if (roles.isAdmin && !adminSelectedUserEmail) return
    // The billing options are decided for the client's workspace, so wait until it is known
    if (roles.isClient && !isWorkspaceResolved) return
    if (isShootingCategory(selectedCategory) && (!selectedCity || !selectedCountryCode)) return
    if (!Object.keys(selectedProducts).length) return

    getInstructionCatalogue.mutate({
      sessionId,
      area: selectedSegment,
      city: selectedCity,
      email: targetUser.email,
      category: selectedCategory,
      countryCode: selectedCountryCode || targetUser.country,
      workspaceId: orderWorkspaceId || null,
      coordinates: selectedAssignmentPlace?.geometry.location,
      productKinds: Array.from(selectedKinds),
      products: Object.values(selectedProducts).map((product) => ({
        id: product.id,
        name: t(`product:p_${product.id}`),
        type: product.type,
        kind: product.kind,
        unitPrice: bigFromFee(product.feePrice).toFixed(FIXED_DECIMAL_PLACES),
        count: product.quantity,
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSelectedUserEmail, isInstructionStepSkipped, getInstructionCatalogue, roles.isAdmin, selectedAssignmentPlace?.geometry.location, selectedCategory, selectedCity, selectedCountryCode, selectedKinds, selectedProductsKey, selectedSegment, sessionId, t, targetUser.country, targetUser.email, orderWorkspaceId, isWorkspaceResolved])

  // Log entering the billing step with what the broker sees (ISSUE-4023), once per mount. The invoice card's state is
  // only known once the billing options arrived, so the event waits for the instruction catalogue to settle; the layout
  // is measured one frame after that render. Whatever happens, the entry is counted exactly once: a catalogue that
  // failed sends the invoice option as unknown (null), options that never settle (a fetch guard that never lifts, a
  // hanging request) send it after a timeout with a Sentry warning, and leaving the screen before any of that sends
  // what is known with the layout unmeasured.
  const entersSentRef = useRef(false)
  const knownInvoiceOptionRef = useRef<InvoiceOption | null>(null)
  const mountedRef = useRef(true)
  const sendEnters = useCallback((invoiceOption: InvoiceOption | null, layout: ScreenEntryLayout) => {
    if (entersSentRef.current) return
    entersSentRef.current = true
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_BILLING_STEP, { invoice_option: invoiceOption, ...layout })
  }, [])

  // The diagnostics the timeout warning reports, as of the moment it fires
  const catalogueDiagnosticsRef = useRef({ status: getInstructionCatalogue.status as string | undefined, isInstructionStepSkipped })
  catalogueDiagnosticsRef.current = { status: getInstructionCatalogue.status, isInstructionStepSkipped }

  // Mount-only: the timeout and the leave fallback belong to the visit, not to the catalogue's state. Declared before
  // the measuring effect so its cleanup runs first on unmount and the late measurement finds the entry already sent.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (entersSentRef.current) return
      logger.warn('Billing options did not settle before the enters-screen event', undefined, {
        context: { component: 'BillingStepController', action: 'billingOptionsUnsettled', metadata: catalogueDiagnosticsRef.current },
      })
      void measureScreenEntryLayout().then((layout) => { if (mountedRef.current) sendEnters(null, layout) })
    }, BILLING_OPTIONS_SETTLE_TIMEOUT_MS)

    return () => {
      mountedRef.current = false
      window.clearTimeout(timer)
      sendEnters(knownInvoiceOptionRef.current, UNMEASURED_SCREEN_ENTRY_LAYOUT)
    }
  }, [sendEnters])

  const isCatalogueSettled = getInstructionCatalogue.isSuccess || getInstructionCatalogue.isError
  // The payment cards only render for a priced order (Billing.component), so a free order offers no invoice card
  const showsPaymentMethods = total.totalAfterDiscount.gt(0)
  const measurementStartedRef = useRef(false)
  useEffect(() => {
    if (!isCatalogueSettled || entersSentRef.current) return

    // Re-evaluated while the measurement is pending, so the latest known state is what gets sent
    knownInvoiceOptionRef.current = getInstructionCatalogue.isError
      ? null
      : showsPaymentMethods
        ? resolveInvoiceOption(availablePaymentMethods, { isAdmin: roles.isAdmin, invoiceAllowed: targetUser.invoiceAllowed })
        : 'not_offered'
    if (measurementStartedRef.current) return
    measurementStartedRef.current = true

    void measureScreenEntryLayout().then((layout) => { if (mountedRef.current) sendEnters(knownInvoiceOptionRef.current, layout) })
  }, [isCatalogueSettled, getInstructionCatalogue.isError, showsPaymentMethods, availablePaymentMethods, roles.isAdmin, targetUser.invoiceAllowed, sendEnters])

  return (
    <TransitionAppear visible>
      <Billing />
    </TransitionAppear>
  )
}
